import React from 'react';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import classNames from 'classnames';
import styles from '@/components/ProjectDetails/Cards/Items/index.less';
import DoTask from '@/components/Lead/LeadTasks/DoTask';
import PrettyDate from '@/components/PrettyDate';
import { Space } from 'antd';
import { useDispatch } from 'umi';
import TaskActions from '@/components/Tasks/TaskActions';
import TaskAssignees from '@/components/Tasks/TaskAssignees';
import { taskState } from '@/components/Tasks/TaskDesignRow';
import { F } from '@/utils/intl';
import { AimOutlined } from '@ant-design/icons';

import rowStyles from '@/components/Tasks/TaskDesignRow.less';

/**
 * Task row inside the project details cards — the same task attributes as the
 * tasks page (state chip, assignees), fitted into the card item design. The
 * card is narrow, so every action lives in the ⋮ popover rather than as an
 * inline button. No project tag: the card already lives on the lead.
 */
export default function TaskItem({ task, refresh, nextAction }: { task: LeadTaskModel, nextAction?: boolean, refresh: () => void }) {
  const dispatch = useDispatch();

  const state = taskState(task);

  const handleTaskClick = () => {
    dispatch({
      type: 'leadTasks/openTaskDetailsDrawer',
      payload: { task },
    });
  };

  return (
    <div
      onClick={handleTaskClick}
      role={'item-container'}
      className={classNames(
        styles.projectDetailItemContainer,
        styles.taskItemContainer,
        styles.projectDetailItemContainerClickable,
        {
          [styles.nextActionItemContainer]: nextAction,
        },
      )}
    >
      <div className={styles.taskItemContent}>
        {nextAction && (
          <div className={styles.nextActionItemIcon}>
            <AimOutlined />
          </div>
        )}
        <div className={styles.projectDetailItemContent}>
          <div className={styles.projectDetailItemHeader}>
            {nextAction && (
              <div className={styles.nextActionTitle}>
                <F
                  id={'pages.lead.next_action'}
                  defaultMessage={'Next Action'}
                />
              </div>
            )}
            <Space size={8} className={styles.projectDetailItemTitle}>
              <span className={classNames({ [styles.taskItemTitleDone]: state === 'done' })}>
                {task.title}
              </span>
            </Space>
          </div>
          <div className={styles.projectDetailItemBody}>
            <Space size={8} wrap>
              {/* Same state chip as the tasks list */}
              <span className={classNames(rowStyles.dueChip, rowStyles[`due_${state}`])}>
                {state === 'retired' ? (
                  <F id="pages.tasks.retired" defaultMessage="Retired" />
                ) : state === 'done' ? (
                  <F id="pages.tasks.state.done" defaultMessage="Done" />
                ) : state === 'standby' ? (
                  <>
                    <F id="pages.tasks.state.snoozed" defaultMessage="Snoozed" />
                    {task.snooze_until && <> · <PrettyDate date={task.snooze_until} /></>}
                  </>
                ) : task.due_date ? (
                  <PrettyDate date={task.due_date} />
                ) : (
                  <F id="pages.tasks.state.noDue" defaultMessage="No due date" />
                )}
              </span>
              <TaskAssignees task={task} size="small" maxCount={3} />
            </Space>
          </div>
        </div>
      </div>
      <div className={styles.projectDetailItemActions} onClick={(e) => e.stopPropagation()}>
        <DoTask
          className={styles.taskItemDoIcon}
          size={20}
          task={task}
          updateRequest={refresh}
        />
        {/* Narrow card: every action lives in the ⋮ popover, none inline. */}
        <TaskActions
          task={task}
          updateRequest={refresh}
          allActionsInDropdown
          hideActionText
          size="small"
        />
      </div>
    </div>
  );
}
